import React from 'react';
import { tableToolbarStripClass } from '../lib/tableClasses';

/**
 * The toolbar strip above a data table: search, filters and the page's create
 * action(s).
 *
 * It exists because the layout is not the same at both breakpoints and the old
 * `tableToolbarClass` could not express that. It was `flex-col md:flex-row`,
 * so on a phone the search and the actions became two stacked rows and the
 * create button landed a full row below the search — burning vertical space on
 * the screen that has least of it.
 *
 * Here the three slots are reordered instead:
 *
 *   mobile     [ search .................... (+) ]
 *              [ filter                          ]
 *              [ filter                          ]
 *
 *   desktop    [ search  filter  filter ..... (+) ]
 *
 *   desktop,   [ search  filter  filter ..... (+) ]
 *   crowded    [         filter                   ]
 *
 * Pure CSS — `order` plus a full-width filter row is enough, so this needs no
 * viewport hook. The hook belongs in `CreateActions`, which genuinely swaps
 * one control for another.
 *
 * THE CROWDED CASE IS WHY THE FILTER SLOT WRAPS. The row itself is
 * `md:flex-nowrap`, and the search and actions slots both refuse to shrink, so
 * the filter slot is the only item flexbox can squeeze. Its children set fixed
 * widths and `shrink-0` (they have to — a collapsed dropdown is unusable), so a
 * slot that could not wrap simply spilled its children out of its own box and
 * painted them over the create button. Wrapping absorbs the overflow as height
 * instead. `self-start` then keeps the button pinned to the first line rather
 * than drifting to the middle of a two-line toolbar.
 *
 * See documentation/Standards.md §6.
 */

export interface TableToolbarProps {
    /** The search input. Gets the flexible width on mobile. */
    search?: React.ReactNode;
    /**
     * Filters, view switches, date pickers. They drop to their own row(s)
     * below the search on mobile — never competing with it for width.
     */
    filters?: React.ReactNode;
    /** Usually `<CreateActions>`. Stays beside the search on every breakpoint. */
    actions?: React.ReactNode;
    /** DOM id — onboarding tours anchor on these. */
    id?: string;
    className?: string;
}

const TableToolbar: React.FC<TableToolbarProps> = ({ search, filters, actions, id, className = '' }) => (
    <div
        id={id}
        className={`${tableToolbarStripClass} p-4 flex flex-wrap md:flex-nowrap items-center gap-3 ${className}`}
    >
        {search ? <div className="order-1 flex-1 min-w-0 md:flex-none md:w-64 md:self-start">{search}</div> : null}
        {/* Sits right after the search on mobile so the two share one row; on
            desktop `order-3` pushes it to the far end. */}
        {actions ? (
            <div className="order-2 shrink-0 flex items-center gap-2 md:order-3 md:ml-auto md:self-start">{actions}</div>
        ) : null}
        {filters ? (
            <div className="order-3 w-full md:order-2 md:w-auto md:flex-1 flex flex-col md:flex-row md:flex-wrap gap-3 md:items-center min-w-0">
                {filters}
            </div>
        ) : null}
    </div>
);

export default TableToolbar;
